import {
    currentUnixTimestamp,
    genId,
    jsRedirect,
    resizeImage,
    delay,
    stripCharsFromBeginning,
    isBlankValue,
    urlQuery,
    unixTimestampToReadableDate,
    toTitleCase,
    jsRefresh,
} from '@/js/util.js';

import {
    assert,
    assertArray,
    assertEquals,
    assertFloat,
    assertIdOrInteger,
    assertIn,
    assertIncludes,
    assertNonemptyObject,
    assertNonemptyString,
    assertNotIn,
    assertObject,
    parseIntStrict,
    tryParseIntStrict,
} from '@/js/util/asserts.js';

import {
    SITE_SETTINGS,
    SITE_SETTINGS_DEFAULTS,
    DEFAULT_ASSET_VALUES_AFTER_AUTOFILL,
    EXPORT_FIELDS_SHOWN_IN_ASSET_TABLE,
    ASSET_NAME,
    DEFAULT_USER_EMAIL,
    ATTRIBUTE_INTERNAL_NAMES_TO_NOT_AUTOPOPULATE,
    ProjectType,
    MAP_STATUS,
    AttributeType,
    AssetType,
    ASSET_TYPES,
    EXPORT_FIELD_PRETTY_NAMES,
    PROJECT_FEATURE_NAME,
    SAMPLE_STATUS,
    secondsIn,
} from '@/js/util/shared_constants.js';

import {
    Button,
    ConfirmationModal,
    ErrorModal,
    InfoModal,
    Modal
} from '@/js/classes/modal.js';

import { getStagedProjectIds, ProjectStaging } from '@/js/classes/project_staging.js';
import { AssetForm } from '@/js/classes/asset_form.js';
import { Asset } from '@/js/classes/asset.js';
import { getAttributeObj } from '@/js/classes/attribute.js';
import { DataDisplay } from '@/js/classes/datadisplay.js';

import { cloneDeep } from 'lodash-es';
import { setTag as sentrySetTag, captureException as sentryCaptureException } from '@sentry/browser';
import localforage from 'localforage';


const DEBUG_AUTOPOPULATE = false;
const LOCALSTORAGE_PREFIX = 'newcollector_';
const SETTINGS_LOCALSTORAGE_KEY = LOCALSTORAGE_PREFIX+'settings_';
let last_asset_values = null;
let last_asset_type = null;
let is_saving = false;
let modified_site_settings_attributes = null;
let current_user_email = null;
let current_user_roles = null;
let urlParams = null;
let loaded_images = {};
const refetch_localforage_store = localforage.createInstance({ name: 'newcollector_refetch' });
let cached_staff_access_project_ids = null;
let is_mobile_device = null;
let print_whitelist_style_elem = null;


export const getUrlParam = (param_name, alert_if_missing=false) => {
    if(urlParams===null) { urlParams = new URLSearchParams(window.location.search); }
    const result = urlParams.get(param_name);
    if(!result || result==='') {
        if(alert_if_missing) { alert('Missing url param `'+param_name+'`'); }
        return '';
    }
    return result;
}

export const loadCurrentProject = async () => {
    const projectid_urlparam = getUrlParam('project_id');
    if(!projectid_urlparam) {
        alert('Missing project ID, go back to the dashboard and select a project.');
        jsRedirect('index.html');
        throw new Error('HALT_FOR_URL_REDIRECT');
    }

    const project_id = tryParseIntStrict(projectid_urlparam);
    if(!getStagedProjectIds().includes(project_id)) {
        alert('Invalid project ID, go back to the dashboard and select a project.');
        jsRedirect('index.html');
        throw new Error('HALT_FOR_URL_REDIRECT');
    }

    sentrySetTag('project_id', project_id);
    const staged_project = new ProjectStaging(project_id);
    await staged_project.loadFromClient();

    if(staged_project.metadata.type !== window.newcollector['current_collector_type']) {
        alert('Project ID does not belong to this collector type, go back to the dashboard and select a project.');
        jsRedirect('index.html');
        throw new Error('HALT_FOR_URL_REDIRECT');
    }

    return staged_project;
}

export const getDisplayFields = (staged_project, asset_type) => {
    const ret = [];
    const used_export_fields = staged_project.getExportFields(asset_type);
    const possible_fields = EXPORT_FIELDS_SHOWN_IN_ASSET_TABLE[asset_type] ?? EXPORT_FIELDS_SHOWN_IN_ASSET_TABLE[AssetType.Any];
    for(const export_field of possible_fields) {
        if(used_export_fields.includes(export_field)) { ret.push(export_field); }
    }
    return ret;
}

export const getCustomAttribName = (name) => {
    return stripCharsFromBeginning(name, ('custom_000000000000000000000000_').length);
}

export const editAssetModal = async (options) => {
    assertIn('is_new', options);
    assertIn('staged_project', options);
    assertIn('asset', options);
    assertIn('asset_type', options);
    assertIn('values', options);
    assertIn('save_callback', options);
    assertIn('delete_callback', options);

    const is_new = options.is_new;
    const staged_project = options.staged_project;
    const asset = options.asset;
    const asset_type = options.asset_type;
    const values = options.values;
    const save_callback = options.save_callback;
    const delete_callback = options.delete_callback;
    const cancel_callback = options['cancel_callback'] ?? null;

    assertIncludes(ASSET_TYPES, asset_type);

    const custom_attributes = staged_project.metadata.custom_attributes[asset_type];
    const used_attribute_internal_names = staged_project.metadata.used_attribute_internal_names[asset_type];
    if(!is_new) { await staged_project.files.downloadFilesForAsset(asset.asset_id); }
    const files = staged_project.files;

    const settings = window.newcollector.settings;
    const attributes = cloneDeep(window.newcollector.collector_attributes[asset_type]);
    const asset_name = ASSET_NAME[asset_type];

    let did_autopopulate = false;
    if(window.newcollector.settings['auto_populate']==='Yes') {
        if(!is_new) {
            if(DEBUG_AUTOPOPULATE) { console.log('Opened existing asset, setting last_asset_values to:', values); }
            last_asset_values = cloneDeep(values);
            last_asset_type = asset_type;
        }

        if(is_new && last_asset_values !== null && asset_type === last_asset_type) {
            if(DEBUG_AUTOPOPULATE) {
                console.log('Opened new asset, setting values to:', last_asset_values);
                console.log('Original passed values were:', values);
            }
            for(const internal_name in last_asset_values) {
                if(internal_name in values) { continue; }
                if((asset_type in ATTRIBUTE_INTERNAL_NAMES_TO_NOT_AUTOPOPULATE) && ATTRIBUTE_INTERNAL_NAMES_TO_NOT_AUTOPOPULATE[asset_type].includes(internal_name)) { continue; }
                values[internal_name] = last_asset_values[internal_name];
                did_autopopulate = true;
            }
        }
    }


    // Apply per-project-type changes/conversions to attributes/values before displaying form
    if(asset_type===AssetType.Tree) {
        if(is_new) {
            // This auto-increment ID logic is also done in contextMenuCallback_move
            values.tree_id = staged_project.getNextAssetAutoIncrement();
        }
    }


    // Populate default values (if not autofilled from last asset)
    if(asset_type in DEFAULT_ASSET_VALUES_AFTER_AUTOFILL) {
        for(const internal_name in DEFAULT_ASSET_VALUES_AFTER_AUTOFILL[asset_type]) {
            if( (!(internal_name in values)) || isBlankValue(values[internal_name])) {
                values[internal_name] = DEFAULT_ASSET_VALUES_AFTER_AUTOFILL[asset_type][internal_name];
            }
        }
    }


    const edit_asset_modal = new Modal(( (!staged_project.canEditAssets()) ? 'View ' : is_new ? 'Create ' : 'Edit ')+asset_name);
    edit_asset_modal._setup();
    const asset_form_container = document.createElement('div');

    if(is_new) {
        values.time_created = currentUnixTimestamp();
        values.time_modified = values.time_created;
    }

    const button_row1 = document.createElement('div');
    const button_row2 = document.createElement('div');
    /* Add buttons */ {
        button_row1.classList.add('flex-row-space-evenly');
        button_row1.style.marginTop = '40px';
        button_row1.style.gap = '20px';

        button_row2.classList.add('flex-row-space-evenly');
        button_row2.style.marginTop = '20px';

        const cancel_button = new Button('Cancel', ['btn-warning', 'asset-form-button']);
        const delete_button = new Button('Delete '+asset_name, ['btn-danger', 'btn-sm']);
        const save_button = new Button(((is_new ? 'Create ' : 'Save ')+asset_name), ['btn-success', 'asset-form-button']);
        const top_save_button = new Button(((is_new ? 'Create ' : 'Save ')), ['btn-success', 'btn-sm']);
        const form_buttons = [cancel_button, delete_button, save_button, top_save_button];

        const lockFormButtons = () => form_buttons.forEach((button) => button.disable());
        const unlockFormButtons = () => form_buttons.forEach((button) => button.enable());

        const asset_form_save_button_callback = (async function(save_button_clicked) {
            lockFormButtons();
            edit_asset_modal.hideCloseButton();

            const values = await asset_form.validateForm();
             if(values === false) {
                 unlockFormButtons();
                 edit_asset_modal.showCloseButton();
                 return;
             }

            console.log('Asset values after saving:', values);
            try {
                if(!('asset_type' in values)) {
                    values['asset_type'] = asset_type;
                    console.error('Asset was missing asset_type:', values);
                } else if(!ASSET_TYPES.includes(values['asset_type'])) {
                    values['asset_type'] = asset_type;
                    console.error('Asset had invalid asset_type:', values);
                }

                const asset_id = (is_new ? genId() : asset.asset_id);
                const updated_asset = new Asset(asset_id);
                updated_asset.setProject(staged_project);
                updated_asset.setData(values);

                if(!is_new) { updated_asset.setValue('time_modified', currentUnixTimestamp()); }

                try {
                    const asset_photos_added = asset_form.getAddedImages();
                    const photo_ids = Object.keys(asset_photos_added);
                    if(photo_ids.length > 0) {
                        save_button_clicked.startLoading();
                        const resized_blobs = {};
                        for(const photo_id of photo_ids) { resized_blobs[photo_id] = await resizeImage(asset_photos_added[photo_id]); }
                        await staged_project.files.addFilesFromUserUpload(asset_id, resized_blobs, currentUnixTimestamp());
                        for(const photo_id of photo_ids) { delete asset_photos_added[photo_id]; }
                        save_button_clicked.stopLoading();
                    }
                } catch(err) {
                    console.log(err);
                    sentryCaptureException(err);
                    new ErrorModal(`<h5>Error saving asset files on device.</h5>`).show();

                    save_button_clicked.stopLoading();
                    unlockFormButtons();
                    edit_asset_modal.showCloseButton();
                    return;
                }

                if(save_callback) { await save_callback(asset_id, updated_asset); }

                /* Save anything per-project-type */ {
                    // This auto-increment ID logic is also done in contextMenuCallback_move
                    if(asset_type===AssetType.Tree && is_new) { staged_project.markAutoIncrementUsed(updated_asset.getValue('tree_id')); }
                }

                await staged_project.pruneFileCache(asset_id); // Must be done after asset is saved in save_callback
                await staged_project.saveOnClient();

                asset_form.cleanup();
                edit_asset_modal.remove();
                if(DEBUG_AUTOPOPULATE) { console.log('Saved asset, set last_asset_values to:', values); }
                last_asset_values = values;
                last_asset_type = asset_type;
            } catch(err) {
                console.log(err);
                sentryCaptureException(err);
                new ErrorModal(`<h5>Error saving asset</h5>`).show();
            }
            unlockFormButtons();
            edit_asset_modal.showCloseButton();
        });

        save_button.elem.addEventListener('click', () => { asset_form_save_button_callback(save_button); });
        top_save_button.elem.addEventListener('click', () => { asset_form_save_button_callback(top_save_button); });

        button_row1.appendChild(cancel_button.elem);

        if(staged_project.canEditAssets()) {
            button_row1.appendChild(save_button.elem);

            edit_asset_modal.heading_elem_right.prepend(top_save_button.elem);
            top_save_button.elem.style.alignSelf = 'flex-start';
            top_save_button.elem.style.flexShrink = '0';
        }

        if(!is_new && staged_project.canEditAssets()) {
            button_row2.appendChild(delete_button.elem);
        }

        /* Handle Cancel button */ {
            cancel_button.elem.addEventListener('click', function() {
                asset_form.cleanup();
                edit_asset_modal.remove();
                if(cancel_callback) { cancel_callback(); }
            });
        }

        /* Handle Delete button */ {
            delete_button.elem.addEventListener('click', async function() {
                const confirmation_popup = new ConfirmationModal('Delete '+asset_name, 'Are you sure you want to permanently delete this '+asset_name+'?');
                edit_asset_modal.hide();
                confirmation_popup.show();

                confirmation_popup.onClose(function() {
                    edit_asset_modal.show();
                    confirmation_popup.remove();
                });

                confirmation_popup.onConfirm(async function() {
                    confirmation_popup.hideCloseButton();
                    confirmation_popup.disableConfirmationButton();
                    confirmation_popup.startLoading();
                    await staged_project.deleteAsset(asset.asset_id);
                    await staged_project.saveOnClient();
                    delete_callback(asset);
                    asset_form.cleanup();
                    edit_asset_modal.remove();
                    confirmation_popup.remove();
                });
            });
        }
    }

    edit_asset_modal.onClose(() => {
        edit_asset_modal.remove();
        if(cancel_callback) { cancel_callback(); }
    });

    /* Add info to top of form */ {
        const heading_text = document.createElement('span');
        heading_text.style.fontSize = '15px';
        heading_text.innerHTML = `<img class="info-icon">`;
        heading_text.append(`Project: [${staged_project.project_id}] ${staged_project.properties.name}; ${staged_project.numAssets()} ${staged_project.featureName()}s.`);
        edit_asset_modal.appendNode(heading_text);
        if(staged_project.canEditProject()) {
            edit_asset_modal.appendHtml(`
                <span style="font-size: 15px;">
                    <img class="info-icon">
                    <strong>Customize</strong> your <strong>Form</strong> via Edit Project
                </span>
            `);
        }
        edit_asset_modal.appendHtml(`
            <span style="font-size: 15px;">
                <img class="info-icon">
                <span class="red fw-bold">*</span> indicates required field.
            </span>
        `);
    }

    const asset_form = new AssetForm({
        'form_elem': asset_form_container,
        'attributes': (([...attributes, ...custom_attributes]).filter(attribute => used_attribute_internal_names.includes(attribute.internal_name))),
        'values': values,
        'is_new': is_new,
        'files': files,
    });
    edit_asset_modal.appendNode(asset_form_container);
    edit_asset_modal.show();
    asset_form.generateForm();

    if(asset_form.hasCategory('system')) {
        const system_category = asset_form.getCategory('system');

        if(!is_new) {
            const attribute_obj = getAttributeObj({
                'attribute': {
                    'attribute_type': AttributeType.String,
                    'category_name': 'system',
                    'internal_name': 'asset_id',
                    'name': 'Asset ID',
                    'readOnly': true,
                    'required': false,
                },
                'value': asset.asset_id,
            });
            system_category.category_body_elem.appendChild(attribute_obj.setup());
        }

        system_category.collapse();
        system_category.hideClearButton();

        if(window.newcollector.settings['asset_system_category'] !== 'Show') { system_category.hide(); }
    }
    if(asset_form.hasCategory('miscellaneous')) {
        asset_form.getCategory('miscellaneous').hideClearButton();
    }

    edit_asset_modal.appendNode(button_row1);
    edit_asset_modal.appendNode(button_row2);
}

export const chooseAssetTypeModal = (asset_types_arr) => {
    return new Promise((resolve) => {
        const choose_asset_modal = new Modal('Choose');

        const asset_type_choices = {};
        for(const asset_type of asset_types_arr) { asset_type_choices[asset_type] = ASSET_NAME[asset_type]; }

        const attribute_obj = getAttributeObj({
            'attribute': {
                'attribute_type': AttributeType.KeyedButtonSingleOption,
                'category_name': '',
                'internal_name': 'asset_type',
                'name': 'Asset Type:',
                'readOnly': false,
                'required': true,
                'neverWrap': true,
                'options': asset_type_choices,
            },
            'value': null,
        });

        const attribute_elem = attribute_obj.setup();

        for(const input_elem of attribute_elem.querySelectorAll('input')) {
            input_elem.addEventListener('click', () => {
                const asset_type = input_elem.value;
                choose_asset_modal.remove();
                resolve(asset_type);
            });
        }

        choose_asset_modal.appendNode(attribute_elem);
        choose_asset_modal.show();
    });
}

export const refetchAfter = async (name, seconds_to_store, get_data_callback) => {
    const LOCALFORAGE_KEY = 'newcollector_refetch_'+name;
    let local_data = await refetch_localforage_store.getItem(LOCALFORAGE_KEY);
    const current_time = currentUnixTimestamp();

    if(local_data) {
        if(!isOnline()) {
            console.log('Not online, so not refetching.');
            return local_data['fetched_data'];
        }
        const last_checked_time = parseIntStrict(local_data['last_fetched']);
        if(current_time - last_checked_time < seconds_to_store) {
            return local_data['fetched_data'];
        }
    } else {
        local_data = {};
    }

    const fetched_data = await get_data_callback(local_data);
    local_data['last_fetched'] = current_time;
    local_data['fetched_data'] = fetched_data;
    await refetch_localforage_store.setItem(LOCALFORAGE_KEY, local_data);
    return fetched_data;
}

export const loadCollectorAttributes = async () => {
    window.newcollector.collector_attributes = (await refetchAfter(
        'collector_attributes',
        secondsIn.minutes(5),
        async (local_data) => {
            const all_collectors_attributes_hash = ('fetched_data' in local_data ? local_data['fetched_data']['all_collectors_attributes_hash'] : '')
            const attributes_response_data = await handleFetchErrors(
                apiFetch({
                    path: '/get_collectors_attributes',
                    method: 'GET',
                    query: {
                        'all_collectors_attributes_hash': all_collectors_attributes_hash,
                    },
                    timeoutMs: 30000,
                }),
                'getting collector attributes',
            );

            if(attributes_response_data === null) {
                if(local_data && ('fetched_data' in local_data)) {
                    assertIn('all_collectors_attributes_hash', local_data['fetched_data']);
                    assertIn('collectors_attributes_list', local_data['fetched_data']);
                    assertNonemptyString(local_data['fetched_data']['all_collectors_attributes_hash']);
                    assertNonemptyObject(local_data['fetched_data']['collectors_attributes_list']);

                    return local_data['fetched_data'];
                }
                throw new Error('attributes_response_data was null');
            }

            if(attributes_response_data['message'] === 'data_unchanged') {
                return local_data['fetched_data'];
            }

            assertIn('all_collectors_attributes_hash', attributes_response_data);
            assertIn('collectors_attributes_list', attributes_response_data);
            assertNonemptyString(attributes_response_data['all_collectors_attributes_hash']);
            assertNonemptyObject(attributes_response_data['collectors_attributes_list']);

            return {
                'all_collectors_attributes_hash': attributes_response_data['all_collectors_attributes_hash'],
                'collectors_attributes_list': attributes_response_data['collectors_attributes_list'],
            };
        },
    ))['collectors_attributes_list'];

    window.newcollector.collector_attributes_lookup = {};
    for(const asset_type in window.newcollector.collector_attributes) {
        window.newcollector.collector_attributes_lookup[asset_type] = {};
        for(const attribute of window.newcollector.collector_attributes[asset_type]) {
            window.newcollector.collector_attributes_lookup[asset_type][attribute.internal_name] = attribute;
        }
    }
}

export const getCoordsFromAddress = async (address) => {
    const response = await concurrencyLimitedApiFetch({
        path: '/util/get_coords_from_address',
        method: 'GET',
        query: { address: address },
        timeoutMs: 8000,
    });
    const response_data = await response.json();
    if(response_data.status === 'error') { throw new Error(response_data.message); }
    if(response_data.result === 'no_results') { return null; }
    return response_data.result;
}

export const getAddressFromCoords = async (opts) => {
    assertIn('lng', opts);
    assertIn('lat', opts);
    assertFloat(opts['lng']);
    assertFloat(opts['lat']);
    const response_data = await handleFetchErrors(
        concurrencyLimitedApiFetch({
            path: '/util/get_address_from_coords',
            method: 'GET',
            query: {
                'lng': opts['lng'],
                'lat': opts['lat'],
            },
            timeoutMs: 8000,
        }),
        'getting reverse geocode result',
    );

    if(response_data===null) { return 'Reverse Geocode Failed'; }

    if(response_data.result === 'no_results') {
        return 'No address found';

    } else if('formattedAddress' in response_data.result) {
        return response_data.result['formattedAddress'];

    } else {
        console.error('Reverse geocoding failed: ', response_data);
        new ErrorModal(`Reverse geocoding failed. Check your coordinates or try again later.`).show();
        return 'Reverse Geocode Failed';
    }
}

export const isOnline = () => {
    assert('settings' in window.newcollector, 'settings not in window.newcollector');
    if(window.newcollector.settings['offline_mode']==='Yes') { return false; }
    return navigator.onLine;
}

export const getCurrentUser = async () => {
    if(!isOnline()) { return console.log('Offline, not getting current user.'); }
    const current_user_data = await handleFetchErrors(
        concurrencyLimitedApiFetch({
            path: '/get_current_user',
            method: 'GET',
        }),
        'getting current user info',
        null
    );
    if(!current_user_data) { return; }

    current_user_email = current_user_data['email'];
    localStorage.setItem(LOCALSTORAGE_PREFIX+'current_user_email', current_user_email);

    current_user_roles = current_user_data['roles'];
    localStorage.setItem(LOCALSTORAGE_PREFIX+'current_user_roles', JSON.stringify(current_user_roles));
}

export const getUserEmail = () => {
    if(current_user_email !== null) { return current_user_email; }
    current_user_email = localStorage.getItem(LOCALSTORAGE_PREFIX+'current_user_email') ?? DEFAULT_USER_EMAIL;
    return current_user_email;
}

export const getUserRoles = () => {
    if(current_user_roles !== null) { return current_user_roles; }
    const stored_roles = localStorage.getItem(LOCALSTORAGE_PREFIX+'current_user_roles');
    try {
        current_user_roles = stored_roles ? JSON.parse(stored_roles) : [];
    } catch (err) {
        current_user_roles = [];
    }
    return current_user_roles;
}

export const userIsAdmin = () => {
    const roles = getUserRoles();
    return roles.includes('collector-admin');
}

export const userIsStaff = (project_type) => {
    const roles = getUserRoles();
    if(project_type==='any') { return roles.some(role => role.endsWith('-staff')); }
    return roles.includes(project_type+'-staff');
}

export const isStaffAccessing = (project_id) => {
    assertIdOrInteger(project_id);
    return getStaffAccessLoadedProjectIds().includes(project_id);
}

export const getStaffAccessLoadedProjectIds = () => {
    if(!cached_staff_access_project_ids) {
        cached_staff_access_project_ids = JSON.parse(localStorage.getItem(LOCALSTORAGE_PREFIX+'staff_access_loaded_project_ids') ?? '[]');
    }
    return cached_staff_access_project_ids;
}

export const setStaffAccessLoadedProjectIds = (project_ids) => {
    assertArray(project_ids);
    cached_staff_access_project_ids = project_ids;
    localStorage.setItem(LOCALSTORAGE_PREFIX+'staff_access_loaded_project_ids', JSON.stringify(project_ids));
}

export const addProjectIdToStaffAccess = (new_project_id) => {
    assertIdOrInteger(new_project_id);
    const new_loaded_project_ids = [...(new Set([
        ...getStaffAccessLoadedProjectIds(),
        new_project_id,
    ]))];

    setStaffAccessLoadedProjectIds(new_loaded_project_ids);
}

export const addProjectIdsToStaffAccess = (new_project_ids) => {
    assertArray(new_project_ids);
    const new_loaded_project_ids = [...(new Set([
        ...getStaffAccessLoadedProjectIds(),
        ...new_project_ids,
    ]))];

    setStaffAccessLoadedProjectIds(new_loaded_project_ids);
}

export const loadSettings = () => {
    const settings = localStorage.getItem(SETTINGS_LOCALSTORAGE_KEY+window.newcollector['current_collector_type']);
    if(!settings) {
        window.newcollector.settings = SITE_SETTINGS_DEFAULTS;
        return;
    }

    window.newcollector.settings = JSON.parse(settings);
}

export const setSettings = (new_settings) => {
    localStorage.setItem(SETTINGS_LOCALSTORAGE_KEY+window.newcollector['current_collector_type'], JSON.stringify(new_settings));
}

export const getSiteSettingsAttributes = () => {
    if(modified_site_settings_attributes) { return modified_site_settings_attributes; }
    const attributes = cloneDeep(SITE_SETTINGS);

    const tree_attributes = window.newcollector.collector_attributes[ProjectType.Tree];
    const tree_species_attribute = tree_attributes.find((attribute) => attribute.internal_name==='species');
    const settings_pinned_tree_species_attribute = attributes.find((attribute) => attribute.internal_name==='pinned_tree_species');
    settings_pinned_tree_species_attribute.options = tree_species_attribute.options;

    attributes.find((attribute) => attribute.internal_name==='create_new_assets_from_data_page')
        .name = `Create New ${PROJECT_FEATURE_NAME[window.newcollector['current_collector_type']]}s from Data Table page`;

    attributes.find((attribute) => attribute.internal_name==='assets_per_page')
        .name = `${PROJECT_FEATURE_NAME[window.newcollector['current_collector_type']]}s displayed per page`;

    modified_site_settings_attributes = attributes;
    return attributes;
}

export const save_button_click = async (staged_project, map_save_button, map_save_button_icon) => {
    if(!isOnline()) { return console.log('Offline, not saving.'); }
    if(is_saving) { return console.log('Already saving.'); }
    is_saving = true;
    let needs_to_save_anything = false;

    try {
        const needs_saved_to_server = staged_project.hasUnsavedProjectChanges();
        const has_pending_ops = staged_project.hasPendingOps();
        const has_unsaved_asset_files = staged_project.hasUnsavedAssetFiles();
        needs_to_save_anything = (needs_saved_to_server || has_pending_ops || has_unsaved_asset_files);

        if(needs_to_save_anything) {
            map_save_button_icon.src = 'static/icons/hourglass-half.svg';
        }

        if(needs_saved_to_server) {
            console.log('[loadProjects] Saving unsaved projects.');
            await staged_project.saveProjectToServer();
        }
        if(has_pending_ops) {
            console.log('[loadProjects] Saving unsaved assets.');
            await staged_project.saveAllPendingOps();
        }
        if(has_unsaved_asset_files) {
            console.log('[loadProjects] Saving unsaved asset files.');
            await staged_project.files.uploadAllAssetFiles();
        }
    } catch(err) {
        console.error('Failed to save: ',err);
        sentryCaptureException(err);
    }

    if(!staged_project.hasUnsavedProjectChanges() && !staged_project.hasUnsavedAssetChanges()) {
        for(const node of document.querySelectorAll('.unsaved-changes')) {
            node.classList.remove('unsaved-changes');
        }
        map_save_button.classList.add('saved-changes');
        map_save_button_icon.src = 'static/icons/check-square.svg';
        await delay(750);
        map_save_button.classList.remove('saved-changes');
        if(needs_to_save_anything && window.newcollector['current_collector_type'] === ProjectType.Lab) {
            new InfoModal(`
                <div>Remember to click the "Submit Sample" button on the <a href="assets.html?project_id=${staged_project.project_id}">Manage Data Page</a> to notify the lab once you are finished.
            `).show();
        }
    } else {
        map_save_button.classList.add('unsaved-changes');
        map_save_button.classList.remove('saved-changes');
    }

    map_save_button_icon.src = 'static/icons/save.svg';
    is_saving = false;
}

export const addAlert = (text, type, parent) => {
    assert((['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark']).includes(type), 'Invalid alert type');
    const alertElem = document.createElement('div');
    alertElem.classList.add('alert', 'alert-'+type, 'my-2', 'mx-4');
    alertElem.role = 'alert';

    const closeBtn = document.createElement('span');
    closeBtn.innerText = '×';
    closeBtn.style.cursor = 'pointer';
    closeBtn.style.float = 'right';
    closeBtn.style.marginLeft = '15px';
    closeBtn.style.fontWeight = 'bold';
    closeBtn.style.lineHeight = '1.2';
    closeBtn.onclick = () => { alertElem.remove(); };

    alertElem.appendChild(closeBtn);
    alertElem.appendChild(document.createTextNode(text));

    if(!parent) { parent = document.getElementById('page-alerts'); }
    parent.appendChild(alertElem);
}

export const handleFetchErrors = async (response_promise, failed_during, context) => {
    // Wrapper for checking fetch response and showing error popup if necessary
    assert(response_promise instanceof Promise);
    let response = null;
    let response_data_text = null;
    let response_data = null;

    try {
        response = await response_promise;
    } catch(err) {
        networkErrorPopup(failed_during, response, null, context);
        console.log('Exception when making request:', err);
        console.log('Request failed (could not decode stream)');
        sentryCaptureException(err);
        return null;
    }

    try {
        response_data_text = await response.text();
    } catch(err) {
        networkErrorPopup(failed_during, response, response_data_text, context);
        console.log('Exception when making request:', err);
        console.log('Request failed (could not decode stream) `'+response_data_text+'`');
        sentryCaptureException(err);
        return null;
    }

    try {
        response_data = JSON.parse(response_data_text);
    } catch(err) {
        networkErrorPopup(failed_during, response, response_data_text, context);
        console.log('Exception when making request:', err);
        console.log('Request failed (could not parse response) `'+response_data_text+'`');
        sentryCaptureException(err);
        return null;
    }

    if(!('message' in response_data)) {
        networkErrorPopup(failed_during, response, response_data_text, context);
        console.error('Request failed (invalid response) `'+response_data_text+'`');
        return null;
    }

    if(!response.ok || (!(['success', 'fail']).includes(response_data['status']))) {
        networkErrorPopup(failed_during, response, response_data['message'], context);
        console.error('Request failed (response indicated error) `'+response_data['message']+'`');
        return null;
    }

    return response_data;
}

export const networkErrorPopup = (failed_during, response, response_message, context) => {
    let response_error_name = 'Unknown Error';
    failed_during = `(while ${failed_during})`;
    if(!response_message) { response_message = 'The server did not respond in the expected manner.'; }
    if(!context) { context = ''; }
    let tip = 'Make a backup of your data, then try again later.';

    if(!response) {
        response_error_name = 'Networking Problem';

    } else if(response.status === 400) {
        response_error_name = 'Bad Request';
        tip = '<strong style="red">Make a backup of your data</strong>, then try again. If the problem persists, clear your cache, then try again.';

    } else if(response.status === 403) {
        response_error_name = 'Lacking Permission';
        if(response_message.includes('Apache Server')) {
            response_message = 'Your Davey login session has expired.';
            context = '';
            tip = '<a href="after_login.html" target="_blank">Click here to log-in in a new tab</a>';
        } else if(response_message.includes('own') || response_message.includes('staff')) {
            tip = 'Contact Davey Institute support to request access.';
        } else {
            tip = 'Ask the project owner to grant you access.';
        }

    } else if(response.status === 500 || response.status === 502 || response.status === 504) {
        response_error_name = 'Server Issue';
        tip += ' Please report if issue continues.';

    }

    if(context !== '') {
        context = `
            <hr>
            <small class="fw-bold">Context:</small>
            <br>
            <small class="my-1">${context}</small>
        `;
    }

    new ErrorModal(
        `
            <h6>${response_message}</h6>
            <div class="my-1">${failed_during}</div>
            ${context}
            <hr>
            <div class="my-1">${tip}</div>
        `,
        response_error_name,
    ).show();
}

export class CountdownBarrier {
    constructor(onBlock, onLift) {
        this.activeTrackers = new Set();
        this.liftPromise = null;
        this.resolveLift = null;
        this.on_block_callback = onBlock ?? null;
        this.on_lift_callback = onLift ?? null;
    }

    acquireBlock(id) {
        if(this.activeTrackers.has(id)) { return; }
        this.activeTrackers.add(id);

        if(this.activeTrackers.size === 1) {
            this.liftPromise = new Promise((resolve) => {
                this.resolveLift = resolve;
            });
            if(this.on_block_callback) { this.on_block_callback(); }
        }
    }

    releaseBlock(id) {
        if(!this.activeTrackers.has(id)) { return; }
        this.activeTrackers.delete(id);

        if(this.activeTrackers.size === 0 && this.resolveLift) {
            this.resolveLift();
            this.liftPromise = null;
            this.resolveLift = null;
            if(this.on_lift_callback) { this.on_lift_callback(); }
        }
    }

    async waitForUnblock() {
        if(this.activeTrackers.size === 0) { return; }
        await this.liftPromise;
    }

    isBlocked() {
        return (this.activeTrackers.size > 0);
    }
}

export class AsyncSemaphore {
    constructor(limit) {
        this.limit = limit;
        this.active = 0;
        this.queue = [];
    }

    async run(task) {
        if(this.active >= this.limit) {
            await new Promise(resolve => this.queue.push(resolve));
        }
        this.active++;
        try {
            return await task();
        } finally {
            this.active--;
            if(this.queue.length > 0) {
                this.queue.shift()();
            }
        }
    }
}

export const apiFetch = async (opts) => {
    assertIn('path', opts);
    assertNonemptyString(opts['path']);
    assert(opts['path'].startsWith('/'));
    let path = opts['path'];

    if('query' in opts) {
        const query = opts['query'];
        assertObject(query);
        path += urlQuery(query);
    }

    assertIn('method', opts);
    assertNonemptyString(opts['method']);
    const method = opts['method'];

    const headers = opts['headers'] ?? {};
    headers['X-Requested-With'] = 'XMLHttpRequest';

    if('contentType' in opts) {
        assertNotIn('Content-Type', headers);
        headers['Content-Type'] = opts['contentType'];
    }

    const fetchInit = {
        method: method,
        headers: headers,
    };

    if('body' in opts) { fetchInit.body = opts['body']; }
    if('timeoutMs' in opts && opts['timeoutMs']!==null) { fetchInit.signal = AbortSignal.timeout(opts['timeoutMs']); }

    return fetch(apihost+path, fetchInit);
}

const fetchLimiter = new AsyncSemaphore(5);

export async function concurrencyLimitedFetch(resource, options = {}) {
    return fetchLimiter.run(() => {
        assertNotIn('signal', options);
        if('timeoutMs' in options) { options.signal = AbortSignal.timeout(options.timeoutMs); }
        return fetch(resource, options);
    });
}

export async function concurrencyLimitedApiFetch(opts) {
    return fetchLimiter.run(() => {
        return apiFetch(opts);
    });
}

export const getSpeciesData = (species_data) => {
    if(!species_data) {
        return {
            'scientific_name': 'Unknown',
            'common_name': 'Unknown',
            'species_type': '',
            'leaf_type': '',
            'percent_leaf_type': '',
            'show': true,
            'engine_compat': false,
        };
    }
    let scientific_name =     species_data[0] ?? 'Unknown';
    let common_name =         species_data[1] ?? 'Unknown';
    let species_type =        species_data[2] ?? '';
    let leaf_type =           species_data[3] ?? '';
    let percent_leaf_type =   species_data[4] ?? '';
    let show =                species_data[5] ?? true;
    let engine_compat =       species_data[6] ?? false;

    return {
        'scientific_name': scientific_name,
        'common_name': common_name,
        'species_type': species_type,
        'leaf_type': leaf_type,
        'percent_leaf_type': percent_leaf_type,
        'show': show,
        'engine_compat': engine_compat,
    };
}

export const getCustomSpeciesData = (serialized) => {
    const parts = serialized.split(':');
    assertEquals(parts[0], 'custom_species');
    const common_name =         parts[1];
    const scientific_name =     parts[2];
    const species_type =        parts[3];

    return {
        'scientific_name': scientific_name,
        'common_name': common_name,
        'species_type': species_type,
        'leaf_type': '',
        'percent_leaf_type': '',
        'show': true,
        'engine_compat': false,
    };
}

export const loadImage = async (src) => {
    if(src in loaded_images) { return loaded_images[src]; }
    try {
        const response = await fetch(src, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if(response.status === 403 || response.redirected) {
            const response_text = await response.text();
            networkErrorPopup('loading image', response, response_text, null);
            return null;
        }

        const objectUrl = URL.createObjectURL(await response.blob());
        return new Promise((resolve, reject) => {
            const img = new Image();
            loaded_images[src] = img;

            img.onload = () => {
                URL.revokeObjectURL(objectUrl);
                resolve(img);
            };

            img.onerror = (err) => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error('Failed to decode image data'));
            };

            img.src = objectUrl;
        });

    } catch(err) {
        return Promise.reject(err);
    }
}

export const addPhotosToZipObj = async (project, zipObj) => {
    await project.files.willNeedData();
    await project.files.downloadAllFiles();
    const failed_files = new Set();
    for(const asset_id of project.files.getAssetIdsWithFiles()) {
        for(const file_id of project.files.getAssetFileIds(asset_id)) {
            try {
                const file_data = project.files.getFileData(file_id);
                const file_blob = project.files.getFileBlob(file_id);
                const filename = project.project_id+'_'+asset_id+'_'+file_id+'_'+file_data['timestamp_uploaded']+'.jpg';
                zipObj[filename] = new Uint8Array(await file_blob.arrayBuffer());
            } catch(err) {
                const file_info = asset_id+':'+file_id;
                console.log(`Failed to load file from device: ${file_info}`);
                sentryCaptureException(err);
                failed_files.add(file_info);
            }
        }
    }
    if(failed_files.size > 0) {
        new ErrorModal(`
            <h5>Failed to load some files from device:</h5>
            <div>Project ID: ${project.project_id}</div>
            <div>Files: ${[...failed_files].join(', ')}</div>
        `).show();
    }
}

export const getProjectPropertiesCsvArr = (project) => {
    return [
        [
            'project_id',
            'name',
            'location',
            'revenue',
            'comments'
        ],
        [
            project.project_id,
            project.properties.name,
            project.properties.location,
            project.properties.revenue,
            project.properties.comments,
        ],
    ];
}

export const getAssetsCsvArr = (project, asset_type) => {
    assertIncludes(ASSET_TYPES, asset_type);
    const assets_csv_arr = [];
    let sorted_export_fields = project.getExportFieldsSorted(asset_type, true);
    assets_csv_arr.push(sorted_export_fields);

    for(const asset of project.getAssets()) {
        if(asset.getType() !== asset_type) { continue; }
        const export_data = asset.getExportData(true);
        const sorted_attribute_values = sorted_export_fields.map(key => export_data[key] ?? '');
        assets_csv_arr.push(sorted_attribute_values);
    }
    return assets_csv_arr;
}

export const populateSubmitProjectModal = (staged_project, modal, response_data) => {
    let api_path;
    let comments_placeholder;
    let support_email;
    const project_type = staged_project.metadata.type;

    if(project_type === ProjectType.Tree) {
        api_path = '/reports/request_project_map';
        comments_placeholder = 'Comments to mappers';
        support_email = 'institute.maps@davey.com';

    } else if(project_type === ProjectType.Lab) {
        api_path = '/reports/submit_project_sample';
        comments_placeholder = 'Comments to lab staff';
        support_email = 'diagnostic.lab@davey.com';

    } else {
        console.error(`Unhandled project_type "${project_type}"`);
        return;
    }

    modal.clearBody();
    modal.appendHtml(`<h5 class="my-3"><strong>Status:</strong> ${response_data['status_label']}</h5>`);

    const comments_elem = document.createElement('textarea');
    comments_elem.classList.add('w-100', 'mb-2');
    comments_elem.style.height = '100px';
    comments_elem.placeholder = comments_placeholder;

    if(response_data['status_value'] === 'NOT_REQUESTED') {
        modal.onConfirm(async () => {
            if(comments_elem.value.length > 1000) {
                alert('Comments cannot exceed 1000 characters.');
                return;
            }

            modal.disableConfirmationButton();
            modal.startLoading();

            const response_data = await handleFetchErrors(
                apiFetch({
                    path: api_path,
                    method: 'POST',
                    contentType: 'application/json',
                    body: JSON.stringify({
                        'project_id': staged_project.project_id,
                        'comments': comments_elem.value,
                    }),
                    timeoutMs: 8000,
                }),
                'submitting project to staff',
                `<div>Project ID: ${staged_project.project_id}</div>`,
            );

            if(response_data===null) {
                modal.remove();
                return;
            }
            modal.stopLoading();
            populateSubmitProjectModal(staged_project, modal, response_data);
        });

        if(staged_project.isOwner() || userIsAdmin() || userIsStaff(staged_project.metadata.type)) { modal.enableConfirmationButton(); }
        modal.setConfirmationButtonText('Submit');

    } else {
        comments_elem.disabled = true;
        comments_elem.value = response_data['comments'];
        modal.setConfirmationButtonText('Already Submitted');

        const timestamp_requested = unixTimestampToReadableDate(response_data['timestamp_requested']);
        modal.appendHtml(`<div><strong>Date Requested: </strong>${timestamp_requested}`);

    }

    modal.appendNode(comments_elem);
    modal.appendHtml(`<div class="mb-2 text-center">If you have any questions, email <a href="mailto:${support_email}">${support_email}</a></div>`);
}

export const populatePendingProjectSubmissionsModal = (async (collector_type, loaded_projects, submissions_modal) => {
    submissions_modal.clearBody();

    let submission_name;
    let api_path;

    if(collector_type === ProjectType.Tree) {
        submission_name = 'Map Request';
        api_path = '/reports/get_pending_map_requests';

    } else if(collector_type === ProjectType.Lab) {
        submission_name = 'Sample Submission';
        api_path = '/reports/get_pending_sample_submissions';

    } else {
        console.error(`Unhandled collector_type "${collector_type}"`);
        return;

    }


    const response_data = await handleFetchErrors(
        apiFetch({
            path: api_path,
            method: 'GET',
            timeoutMs: 8000,
        }),
        'getting project submissions',
    );

    if(response_data===null) { return; }

    const original_accessed_ids = getStaffAccessLoadedProjectIds().sort().join(',');

    const results = response_data['results'];
    assertArray(results);
    if(results.length > 0) {
        const datadisplay_parent = document.createElement('div');
        submissions_modal.appendNode(datadisplay_parent);
        const datadisplay = new DataDisplay({
            'parent_elem': datadisplay_parent,
            'name': submission_name,
            'field_names': ['ID', 'Status', 'Requested By', 'Comments', 'Access', 'Update'],
            'entries_per_page': 20,
            'field_width_overrides': {
                'ID': '0 0 70px',
                'Status': '0 0 130px',
                'Comments': '1.5',
                'Access': '0 0 85px',
                'Update': '0 0 85px',
            },
        });
        datadisplay.setup();

        const accessProject = ((access_button, project_id) => {
            addProjectIdToStaffAccess(project_id);

            access_button.disabled = true;
            access_button.classList.remove('btn-primary');
            access_button.classList.add('btn-outline-primary');
        });

        datadisplay.onEntryRender((nodes) => {
            for(const node of nodes) {
                const project_id = parseIntStrict(node.dataset.entryId);

                try {
                    const access_button = document.createElement('button');
                    access_button.classList.add('btn', 'btn-primary', 'btn-sm');
                    access_button.textContent = 'Access';
                    if(getStaffAccessLoadedProjectIds().includes(project_id)) {
                        access_button.disabled = true;
                        access_button.classList.remove('btn-primary');
                        access_button.classList.add('btn-outline-primary');
                    }

                    access_button.addEventListener('click', (e) => {
                        accessProject(access_button, project_id);
                    });

                    const update_button = document.createElement('button');
                    update_button.classList.add('btn', 'btn-success', 'btn-sm');
                    update_button.textContent = 'Update';

                    handleUpdateProjectSubmissionButton(project_id, collector_type, update_button, async () => {
                        submissions_modal.show();
                        await populatePendingProjectSubmissionsModal(collector_type, loaded_projects, submissions_modal);
                    });

                    update_button.addEventListener('click', (e) => {
                        submissions_modal.hide();
                    });

                    if(node.classList.contains('datadisplay-body-row')) {
                        /* access button */ {
                            const action_cell = node.children[4];
                            assertEquals(action_cell.textContent, 'access-button-placeholder');
                            access_button.style.display = 'block';
                            access_button.style.display = '100px';
                            access_button.style.margin = 'auto';
                            action_cell.innerHTML = '';
                            action_cell.appendChild(access_button);
                        }

                        /* update button */ {
                            const action_cell = node.children[5];
                            assertEquals(action_cell.textContent, 'update-button-placeholder');
                            update_button.style.display = 'block';
                            update_button.style.display = '100px';
                            update_button.style.margin = 'auto';
                            action_cell.innerHTML = '';
                            action_cell.appendChild(update_button);
                        }

                    } else {
                        assert(node.classList.contains('card'));

                        /* access button */ {
                            const action_node = node.children[0].children[4];
                            assertEquals(action_node.textContent, 'Access: access-button-placeholder');
                            action_node.innerHTML = '';
                            access_button.classList.add('mb-1');
                            action_node.appendChild(access_button);
                        }

                        /* update button */ {
                            const action_node = node.children[0].children[5];
                            assertEquals(action_node.textContent, 'Update: update-button-placeholder');
                            action_node.innerHTML = '';
                            action_node.appendChild(update_button);
                        }
                    }

                } catch(err) {
                    console.log('Error setting action button: ', err);
                    sentryCaptureException(err);
                }

            }
        });

        for(const result of results) {
            if(isBlankValue(result['comments'])) { result['comments'] = 'No Comments.'; }
            datadisplay.addEntry(result['project_id'], [
                result['project_id'],
                result['status_value'],
                result['requested_by_email'],
                result['comments'],
                'access-button-placeholder',
                'update-button-placeholder',
            ], [
                result['project_id'],
                result['status_value'],
                result['requested_by_email'],
                result['comments'],
                '',
                '',
            ]);
            datadisplay.afterEntries();
        }

    } else {
        submissions_modal.appendHtml(`No pending ${submission_name.toLowerCase()}s.`);
    }

    submissions_modal.onConfirm(() => {
        const new_accessed_ids = getStaffAccessLoadedProjectIds().sort().join(',');
        submissions_modal.remove();
        if(new_accessed_ids !== original_accessed_ids) { jsRefresh(); }
    });

    submissions_modal.show();
});

export const handleUpdateProjectSubmissionButton = (project_id, project_type, update_button, callback) => {
    let title;
    let comments_placeholder;
    let status_options;
    let api_path_get;
    let api_path_update;

    if(project_type === ProjectType.Tree) {
        title = 'Update Map Request';
        comments_placeholder = 'Comments to mappers';
        status_options = Object.entries(MAP_STATUS);
        api_path_get = '/reports/get_project_map_status';
        api_path_update = '/reports/update_project_map_request';

    } else if(project_type === ProjectType.Lab) {
        title = 'Update Submission Status';
        comments_placeholder = 'Comments to lab staff';
        status_options = Object.entries(SAMPLE_STATUS);
        api_path_get = '/reports/get_project_sample_submission_status';
        api_path_update = '/reports/update_project_sample_submission';

    } else {
        console.error(`Unhandled project_type "${project_type}"`);
        return;

    }

    const populateUpdateProjectSubmissionModal = function(modal, response_data) {
        modal.clearBody();
        modal.stopLoading();
        modal.enableConfirmationButton();

        modal.appendHtml(`<h6 class="my-3"><strong>Current Status:</strong> ${response_data['status_label']}</h6>`);

        const status_text = document.createElement('h6');
        status_text.classList.add('my-3');

        const status_text_name = document.createElement('strong');
        status_text_name.classList.add('me-1');
        status_text_name.textContent = 'New Status:';
        status_text.appendChild(status_text_name);

        const status_select = document.createElement('select');
        status_text.appendChild(status_select);

        for(const [status_key, message] of status_options) {
            const is_selected = (response_data['status_value'] === status_key);
            const opt = new Option(message, status_key, is_selected, is_selected);
            status_select.appendChild(opt);
        }

        const comments_elem = document.createElement('textarea');
        comments_elem.classList.add('w-100', 'mb-2');
        comments_elem.style.height = '100px';
        comments_elem.placeholder = comments_placeholder;
        comments_elem.disabled = true;

        if(response_data['status_value'] !== 'NOT_REQUESTED') {
            comments_elem.value = response_data['comments'];

            const timestamp_requested = unixTimestampToReadableDate(response_data['timestamp_requested']);
            modal.appendHtml(`<div><strong>Date Requested: </strong>${timestamp_requested}`);
        }

        modal.appendNode(comments_elem);
        modal.appendNode(document.createElement('hr'));
        modal.appendNode(status_text);

        modal.onConfirm(async () => {
            modal.disableConfirmationButton();
            modal.startLoading();
            const response_data = await handleFetchErrors(
                apiFetch({
                    path: api_path_update,
                    method: 'POST',
                    contentType: 'application/json',
                    body: JSON.stringify({
                        'project_id': project_id,
                        'new_status': status_select.value,
                    }),
                    timeoutMs: 8000,
                }),
                'setting status',
                `<div>Project ID: ${project_id}</div>`,
            );

            if(response_data===null) {
                modal.remove();
                return;
            }
            populateUpdateProjectSubmissionModal(modal, response_data);
        });
    }

    update_button.addEventListener('click', async function() {
        const update_modal = new ConfirmationModal(title, '', 'Update Status');
        update_modal.appendHtml(`<br><h5>Loading Status...</h5><br>`);
        update_modal.show();

        const response_data = await handleFetchErrors(
            apiFetch({
                path: api_path_get,
                method: 'GET',
                query: { 'project_id': project_id },
            }),
            'getting status',
            `<div>Project ID: ${project_id}</div>`,
        );

        if(response_data===null) {
            update_modal.remove();
            return;
        }

        update_modal.onClose(() => {
            update_modal.remove();
            if(callback) { callback(); }
        });

        populateUpdateProjectSubmissionModal(update_modal, response_data);
    });
}

export const getGeolocation = async () => {
    return new Promise((resolve, reject) => {
        if(!navigator.geolocation) {
            reject(new Error('Geolocation is not supported by your browser'));
            return;
        }

        navigator.geolocation.getCurrentPosition(resolve, (err) => {
            let errorMessage;
            switch(err.code) {
                case err.PERMISSION_DENIED:
                    errorMessage = 'Geolocation permission denied.';
                    break;
                case err.POSITION_UNAVAILABLE:
                    errorMessage = 'Geolocation unavailable.';
                    break;
                case err.TIMEOUT:
                    errorMessage = 'Geolocation request timed out.';
                    break;
                default:
                    errorMessage = err.message || 'Unable to get geolocation.';
                    break;
            }
            reject(new Error(errorMessage));
        });
    });
}

const getExportValPrettyNameFromAttribute = (project, asset_type, field_name) => {
    const attributes = window.newcollector.collector_attributes_lookup[asset_type];
    if(field_name in attributes) { return attributes[field_name].name; }

    const custom_attributes = project.metadata.custom_attributes[asset_type];
    const custom_attribute = custom_attributes.find((attribute) => attribute.internal_name === field_name);
    if(custom_attribute) { return getCustomAttribName(custom_attribute.name); }

    return null;
}

export const getExportValPrettyName = (project, asset_type, field_name) => {
    if(field_name in EXPORT_FIELD_PRETTY_NAMES) { return EXPORT_FIELD_PRETTY_NAMES[field_name]; }

    if(asset_type === AssetType.Any) {
        for(const inner_asset_type of project.getAssetTypes()) {
            const pretty_name = getExportValPrettyNameFromAttribute(project, inner_asset_type, field_name);
            if(pretty_name) { return pretty_name; }
        }
    } else {
        const pretty_name = getExportValPrettyNameFromAttribute(project, asset_type, field_name);
        if(pretty_name) { return pretty_name; }
    }

    console.error('Could not get pretty name for export field `'+field_name+'`');
    return toTitleCase(field_name);
}

export const isMobileDevice = () => {
    if(is_mobile_device === null) {
        const mobileQuery = window.matchMedia('(max-width: 768px)');

        const handleDeviceChange = ((e) => {
            is_mobile_device = !!e.matches;
        });

        handleDeviceChange(mobileQuery);
        mobileQuery.addEventListener('change', handleDeviceChange);
    }

    return is_mobile_device;
}

export const whitelistElemForPrinting = (elem_id) => {
    if(print_whitelist_style_elem) { print_whitelist_style_elem.remove(); }
    print_whitelist_style_elem = document.createElement('style');

    print_whitelist_style_elem.innerHTML = `
        @media print {
            body * { 
                visibility: hidden; 
            }
            
            #${elem_id}, #${elem_id} * { 
                visibility: visible; 
            }
            
            #${elem_id} { 
                position: absolute; 
                left: 0; 
                top: 0; 
                width: 100%;
                margin: 0;
                padding: 0;
            }
        }
    `;

    document.head.appendChild(print_whitelist_style_elem);
}

export const unWhitelistElemForPrinting = () => {
    if(print_whitelist_style_elem) {
        print_whitelist_style_elem.remove();
        print_whitelist_style_elem = null;
    }
}